import React from 'react'
import { Link } from 'react-router-dom'
import PublicLayout from '../components/PublicLayout'

/**
 * Privacy Policy and Terms of Service.
 *
 * The text mirrors docs/drafts/privacy-policy.md and
 * docs/drafts/terms-of-service.md (final 2026-10-16). Change both together,
 * and log it in CHANGELOG.md. Why each statement is there, and the evidence
 * that it is true, is docs/drafts/legal-gap-analysis-2026-10-16.md (§10).
 *
 * ⚠️ Every factual sentence here describes the running system. Twice a vendor
 * or a data flow shipped and the policy went false (Cloudflare's beacon, the
 * Anthropic reranker). Any change that adds a provider, sends client data
 * somewhere new, or keeps it for a different period changes this file in the
 * same commit.
 *
 * Review state (homelab-kb #24): not yet reviewed by a privacy/terms
 * specialist. The owner removed the "Draft" banner on 2026-10-16 regardless.
 *
 * Format choices (gap analysis §12), and what not to undo:
 *   * §10 and §11 of the Terms sit in a bordered box in bold. That is what
 *     makes a disclaimer "conspicuous" (UCC §1-201(b)(10)): contrast with the
 *     surrounding text. Not ALL CAPS, which readers skip (Butterick).
 *   * 16px text in a 60ch column (~75 characters a line). WCAG 1.4.8
 *     asks for no more than 80; the old 3xl column ran to ~98.
 *   * A contents list, links to each provider's own privacy policy, and print
 *     styles (PublicNav and Footer are `print:hidden`), per the California
 *     AG's guidance on privacy policies.
 */

const COMPANY = 'Praenum'
const CONTACT = 'hello@praenum.com'
const MAILING_ADDRESS = '450 Providence Hwy #1158, Dedham, MA 02026'
// If the posting date changes, change these and the matching dates in the
// drafts (and the "What changed" heading in the Privacy Policy).
// November 4 is the app-host cutover (#210); the policy is posted with it. If
// the cutover moves, move this date and the draft's with it.
const PRIVACY_EFFECTIVE = 'November 4, 2026'
const PRIVACY_PREVIOUS = 'October 16, 2026'
const TERMS_EFFECTIVE = 'October 16, 2026'
const TERMS_PREVIOUS = 'July 18, 2026'

// `page` is the route ('privacy' or 'terms'). The contents links spell the full
// path so every navigation target starts with a literal '/'
// (backend/tests/test_navigation_targets.py).
function LegalShell({ title, description, page, effective, previous, contents, children }) {
  return (
    <PublicLayout title={title} description={description}>
      <section className="bg-gradient-to-b from-brand-100 to-white print:bg-none">
        <div className="max-w-[60ch] mx-auto px-4 pt-16 pb-8 print:pt-0">
          <h1 className="text-3xl font-extrabold tracking-tight text-brand-900">{title}</h1>
          <p className="mt-2 text-sm text-gray-600">
            Effective {effective} (previous version: {previous})
          </p>
        </div>
      </section>
      <section className="max-w-[60ch] mx-auto px-4 pb-16">
        <nav aria-label="Contents" className="mb-10 rounded-lg border border-gray-200 p-5 print:hidden">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-600">Contents</h2>
          <ol className="mt-3 space-y-1 text-[15px]">
            {contents.map(([id, label]) => (
              <li key={id}>
                <Link to={`/${page}#${id}`} className="text-brand-600 underline hover:text-brand-500">
                  {label}
                </Link>
              </li>
            ))}
          </ol>
        </nav>
        <div className="space-y-6 text-gray-600 leading-relaxed text-base">{children}</div>
      </section>
    </PublicLayout>
  )
}

function H({ id, children }) {
  return (
    <h2 id={id} className="text-xl font-bold text-brand-900 pt-2 scroll-mt-20">
      {children}
    </h2>
  )
}

function UL({ children }) {
  return <ul className="list-disc pl-6 space-y-2">{children}</ul>
}

function A({ children }) {
  return (
    <a href={`mailto:${CONTACT}`} className="text-brand-600 underline">
      {children || CONTACT}
    </a>
  )
}

function Ext({ href, children }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-brand-600 underline">
      {children}
      <span className="sr-only"> (opens in a new tab)</span>
    </a>
  )
}

// UCC §1-201(b)(10): set off from the surrounding text and in contrasting type.
function Conspicuous({ children }) {
  return (
    <div className="rounded-lg border-2 border-gray-900 p-5 font-bold text-gray-900 space-y-4">
      {children}
    </div>
  )
}

const PRIVACY_CONTENTS = [
  ['who', 'Who this policy covers'],
  ['you-provide', 'Information you provide'],
  ['automatic', 'Information collected automatically'],
  ['public-records', 'Information from public records'],
  ['cookies', 'Cookies and local storage'],
  ['payments', 'Payments'],
  ['use', 'How we use your information'],
  ['sharing', 'Who we share information with'],
  ['where', 'Where your data lives'],
  ['ai', 'Automated matching and AI'],
  ['retention', 'Retention and deletion'],
  ['rights', 'Your choices and rights'],
  ['security', 'Security'],
  ['children', 'Children'],
  ['changes', 'Changes to this policy'],
  ['contact', 'Contact'],
]

const PROVIDERS = [
  {
    name: 'Postmark',
    note: ' (part of ActiveCampaign)',
    href: 'https://www.activecampaign.com/legal/privacy-policy',
    purpose: 'Transactional email delivery',
    data:
      'Your email address, digest content, and open events (the IP address, approximate location, and email app that opened a digest)',
  },
  {
    name: 'Anthropic',
    href: 'https://www.anthropic.com/legal/privacy',
    purpose: 'AI model that ranks your foundation leads',
    data:
      'Your mission description exactly as you wrote it, your state (and city, where we have it), focus areas, and typical grant size range, alongside public IRS data about each foundation. Never your email address, password, or payment details.',
  },
  {
    name: 'Google',
    note: ' (Workspace and Drive)',
    href: 'https://policies.google.com/privacy',
    purpose: 'Our email, and off-site backup storage',
    data:
      'Emails you send us and our replies. Copies of our database, encrypted before they are uploaded with a key Google does not have, so Google cannot read them.',
  },
  {
    name: 'Stripe',
    href: 'https://stripe.com/privacy',
    purpose: 'Subscription billing',
    data: "Billing details you enter on Stripe's pages",
  },
  {
    name: 'Cloudflare',
    href: 'https://www.cloudflare.com/privacypolicy/',
    purpose:
      'Network and security service in front of our site, and cookie-free page-visit analytics',
    data: 'Standard web traffic information',
  },
]

export function Privacy() {
  return (
    <LegalShell
      title="Privacy Policy"
      page="privacy"
      description="What Praenum collects from your nonprofit, why it collects it, who it is shared with, how long it is kept, and how to request deletion."
      effective={PRIVACY_EFFECTIVE}
      previous={PRIVACY_PREVIOUS}
      contents={PRIVACY_CONTENTS}
    >
      <p>
        {COMPANY} ("Praenum", "we", "us") is a grant-monitoring service for US nonprofits. It
        emails a digest of grant opportunities and foundation funding leads matched to the
        profile your organization gives us, and lets you review and search those matches in
        your account. This policy explains what information we collect, how we use it, who we
        share it with, how long we keep it, and the choices you have. Questions: <A />.
      </p>

      <H id="who">Who this policy covers</H>
      <p>
        This policy covers <strong>our customers</strong> (the people who create and use a
        Praenum account on behalf of their organization, including beta testers and free
        trials) and <strong>visitors</strong> to praenum.com and app.praenum.com.
      </p>
      <p>
        Most of what Praenum holds describes organizations, not people: a nonprofit's name,
        location, mission and funding history, much of it from public IRS filings. This policy
        is mainly about information that identifies a person, such as your name or email
        address, and about the text you write for us. It does not cover the websites of the
        foundations, agencies and other organizations we link to.
      </p>

      <H id="you-provide">Information you provide</H>
      <UL>
        <li>
          <strong>Account:</strong> your email address and a password. We store passwords only
          as salted bcrypt hashes, so we cannot read your password.
        </li>
        <li>
          <strong>Organization profile:</strong> organization name, state, a short mission
          description, focus areas, and your typical grant size range. This profile exists to
          match grants to your organization.
        </li>
        <li>
          <strong>Employer Identification Number (EIN), optional.</strong> You can give us your
          organization's EIN, or confirm the one we suggest from public IRS records after you
          confirm your email address. We use it to find your organization in the public IRS
          filings we hold, for example to show which funders have already given to you.
        </li>
        <li>
          <strong>Feedback:</strong> the 👍/👎 relevance marks you give grants and foundation
          leads.
        </li>
        <li>
          <strong>Messages you send us:</strong> if you email us, we keep your message and our
          reply in our email system.
        </li>
      </UL>
      <p>
        Please do not put personal information about individuals, such as the people your
        organization serves, in your mission description. We use it exactly as you write it,
        including sending it to the AI model described under "Automated matching and AI".
      </p>

      <H id="automatic">Information collected automatically</H>
      <UL>
        <li>
          <strong>Email delivery events.</strong> Our email provider (Postmark) reports delivery
          events (bounces and spam complaints), which we record so we stop mailing addresses
          that bounce or complain, as US email law requires. Every digest contains an
          unsubscribe link, and new accounts must confirm their email before receiving digests
          (double opt-in).
        </li>
        <li>
          <strong>Digest opens.</strong> Each digest contains a small invisible image that tells
          Postmark when the email is opened. Postmark receives the technical details of that
          request, such as the IP address, approximate location, and email app. We keep only
          the date and time each digest was first opened, so we can tell whether digests are
          being read and make them more useful. Some email apps load images automatically, so
          an open does not always mean a person read the digest. You can prevent it by turning
          off automatic image loading in your email app. Postmark's link tracking is turned
          off, so the links in a digest are not rewritten or counted. A link that takes you to
          praenum.com or app.praenum.com is recorded in our web server logs like any other visit.
        </li>
        <li>
          <strong>Digest history.</strong> Which digests we sent you, when, what they contained,
          and when each was first opened.
        </li>
        <li>
          <strong>Operational logs.</strong> Our web server records each request (IP address,
          page requested, browser type, time) for security and debugging, and keeps these logs
          for 14 days. Our application's own logs can also record your email address, for
          example when a digest is sent to it; they are kept for 30 days. All logs stay on our
          own servers.
        </li>
        <li>
          <strong>Site analytics.</strong> Our public website uses Cloudflare Web Analytics to
          count page visits. It sets no cookies and stores nothing in your browser, and
          Cloudflare does not use it to fingerprint or profile visitors. We run no advertising
          trackers.
        </li>
      </UL>

      <H id="public-records">Information from public records</H>
      <p>
        The grant and foundation information we match you against comes from public government
        sources: federal grant listings via Simpler.Grants.gov, the SAM.gov federal
        assistance-program catalog, and IRS Form 990 and 990-PF filings.
      </p>
      <p>
        Some of those filings name people. For example, a foundation's filing may name the
        person to contact about applications, with an address or phone number. We show this
        information as the foundation filed it with the IRS.
      </p>

      <H id="cookies">Cookies and local storage</H>
      <p>
        We use one essential cookie: <code>__Host-gs_session</code>, an HttpOnly session cookie that
        keeps you signed in (30-day expiry). It is not readable by page scripts and is not used
        for tracking. Your browser's local storage holds a non-secret convenience copy of your
        organization name and account ID so pages render faster; it is not an authentication
        credential. There are no advertising or cross-site tracking cookies.
      </p>
      <p>
        <strong>Do Not Track.</strong> Some browsers send a "Do Not Track" signal. There is no
        common standard for responding to it, so our site does not change its behavior when it
        receives one. We do not track visitors across other websites either way.
      </p>

      <H id="payments">Payments</H>
      <p>
        Subscriptions are billed through <strong>Stripe</strong>. Checkout and billing
        management happen on Stripe-hosted pages; <strong>your card details never touch our
        servers</strong>. We store only your Stripe customer reference and your subscription
        plan and status. Stripe's handling of your payment data is governed by Stripe's own
        privacy policy.
      </p>

      <H id="use">How we use your information</H>
      <UL>
        <li>
          Match grant opportunities and foundation leads to your profile, send your digest, and
          show your matches in your account.
        </li>
        <li>
          Send transactional email: digests, email-confirmation messages, and account notices
          such as password resets.
        </li>
        <li>
          Rank the foundation leads we find for you, using a third-party AI model (see
          "Automated matching and AI" below).
        </li>
        <li>
          Improve your matches using your feedback. We also use customers' feedback together to
          test whether a change to our matching makes it better or worse for everyone.
        </li>
        <li>Understand whether digests are opened, so we can improve them.</li>
        <li>Operate billing for paid plans.</li>
        <li>
          Tell you about changes to your account, to the service, or to this policy and our
          Terms.
        </li>
        <li>Keep the service secure, prevent abuse, and comply with the law.</li>
      </UL>
      <p>
        We do <strong>not</strong> sell your personal information, and we do not use it for
        advertising. We do not send marketing email to customers beyond the service emails
        described above.
      </p>

      <H id="sharing">Who we share information with</H>
      <p>
        Only the service providers needed to run {COMPANY}, and only the data they need. Each
        provider's name links to its own privacy policy.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border border-gray-200">
          <thead>
            <tr className="bg-gray-50 text-left text-brand-900">
              <th scope="col" className="p-3 border-b border-gray-200 font-semibold">Provider</th>
              <th scope="col" className="p-3 border-b border-gray-200 font-semibold">Purpose</th>
              <th scope="col" className="p-3 border-b border-gray-200 font-semibold">What they process</th>
            </tr>
          </thead>
          <tbody>
            {PROVIDERS.map((p, i) => {
              const cell = i < PROVIDERS.length - 1 ? 'p-3 border-b border-gray-100 align-top' : 'p-3 align-top'
              return (
                <tr key={p.name}>
                  <td className={cell}>
                    <Ext href={p.href}>{p.name}</Ext>
                    {p.note}
                  </td>
                  <td className={cell}>{p.purpose}</td>
                  <td className={cell}>{p.data}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <p>We may also share information:</p>
      <UL>
        <li>
          with professional advisors, such as a lawyer or accountant, when we need their advice,
          under a duty of confidentiality;
        </li>
        <li>
          if the law requires it, or to protect {COMPANY}, our customers or others from fraud,
          abuse or harm;
        </li>
        <li>
          if {COMPANY}'s business is transferred, for example to a company formed to run it, or
          in a merger or sale. Your information would stay protected by this policy.
        </li>
      </UL>

      <H id="where">Where your data lives</H>
      <p>
        {COMPANY}'s application and database run on servers and equipment we own and operate in
        the United States, not on a public cloud. Encrypted copies of the database are also kept
        in Google Drive as an off-site backup. The service providers listed above process the
        data described there on their own systems. {COMPANY} is offered to organizations in the
        United States.
      </p>

      <H id="ai">Automated matching and AI</H>
      <p>
        Matching is automated. Your organization profile (mission text, focus areas, state,
        typical grant size) is processed by rule-based scoring and by machine-learning models of
        two kinds:
      </p>
      <UL>
        <li>
          <strong>Self-hosted models</strong>, which run entirely on our own servers: the
          text-embedding model that finds candidate matches, and the language model that writes
          the short "why this matched" explanations.
        </li>
        <li>
          <strong>A third-party language model, Anthropic's Claude</strong>, which ranks the
          foundation leads we find for you. For each candidate foundation it reads your mission
          description, state (and city, where we have it), focus areas, and typical grant size
          range, alongside that foundation's public IRS filings. Anthropic receives nothing else
          about you: not your email address, password, or payment details. Under Anthropic's
          commercial terms it may not use this data to train its models, and by default it
          deletes it within 30 days.
        </li>
      </UL>
      <p>
        <strong>You can turn off AI ranking.</strong> Email <A /> and we will stop sending your
        profile to Anthropic and delete the rankings it produced for you. Your foundation leads
        are then ordered by our own scoring alone, which in our testing produced noticeably
        fewer useful leads. You can ask us to turn it back on at any time.
      </p>
      <p>
        Match results and explanations are informational suggestions, not eligibility
        determinations, and no automated decision we make has legal effect on you.
      </p>

      <H id="retention">Retention and deletion</H>
      <p>
        We keep your information while your account is open. If you cancel, we keep your profile
        and history so you can come back, until you ask us to delete it.
      </p>
      <p>
        <strong>To delete your personal information,</strong> email <A /> from your account's
        email address. Within 30 days we will delete your email address, your password, and the
        mission description you wrote, along with what we derived from it: the numerical summary
        we use for matching and the AI model's notes on your leads. Then we close the account.
      </p>
      <p>We keep:</p>
      <UL>
        <li>
          <strong>Records about your organization rather than about you:</strong> its name, EIN,
          city and state, focus areas, plan and billing status, and the history of the matches
          and digests we produced for it. Most of this is public record, and we keep it as a
          business record.
        </li>
        <li>
          <strong>Billing records</strong> held by Stripe, such as invoices.
        </li>
        <li>
          <strong>Your email address on our email provider's suppression list</strong>, if it
          ever bounced or you marked a digest as spam, so that it is never emailed again.
        </li>
      </UL>
      <p>
        Deleted information can remain in our backups until those backups are replaced, which
        takes up to 14 days. If we ever restore a backup, we delete your information again. Our
        logs are deleted on the schedule described under "Operational logs".
      </p>

      <H id="rights">Your choices and rights</H>
      <UL>
        <li>
          Review and update your profile, email address and password in your account at any
          time.
        </li>
        <li>Ask for a copy of the personal information we hold about you.</li>
        <li>Ask us to correct it, if it is wrong and you cannot change it yourself.</li>
        <li>Ask us to delete it, as described above.</li>
        <li>Turn off AI ranking, as described above.</li>
        <li>
          Unsubscribe from digests using the link in every email. Your account remains; digests
          stop.
        </li>
        <li>Cancel your subscription at any time from the billing portal.</li>
      </UL>
      <p>
        To make a request, email <A /> from the address on your account, so we know it comes
        from you. We will respond within 30 days. We will not charge you more or refuse you
        service because you made a request.
      </p>
      <p>
        Some states give their residents legal rights over their personal information. We honor
        the requests above from any customer, wherever they are.
      </p>

      <H id="security">Security</H>
      <p>
        Connections are encrypted (TLS); passwords are hashed with bcrypt; session cookies are
        HttpOnly and Secure; the backups we keep with Google are encrypted before they are
        uploaded; access to production systems is limited to the operator. No method of storage
        or transmission is perfectly secure, but we design conservatively and collect little.
      </p>

      <H id="children">Children</H>
      <p>
        {COMPANY} is a business tool for nonprofit staff and is not directed to children under
        16; we do not knowingly collect their information.
      </p>

      <H id="changes">Changes to this policy</H>
      <p>
        We will post any updated version here with a new effective date, and for material
        changes we will tell you in the digest or by email.
      </p>
      <p>
        <strong>What changed on {PRIVACY_EFFECTIVE}.</strong> The part of {COMPANY} you sign in
        to moved to app.praenum.com, and the session cookie is now named __Host-gs_session.
        Nothing new is collected.
      </p>
      <p>
        <strong>What changed on {PRIVACY_PREVIOUS}.</strong> That version added: which of your
        details are required (your typical grant range now is) and that you can give us your
        organization's EIN; how to turn off AI ranking; that Google handles our email and holds
        our encrypted backups; how long we keep logs and backups; a clearer description of what
        deletion removes and what we keep; how to make a request and when we will answer; and
        the public filings we show, some of which name people.
      </p>
      <p>
        <strong>What changed on September 24, 2026.</strong> That version described three things
        the previous version (July 18, 2026) did not: that since September 8, 2026 we send parts
        of your organization profile to Anthropic to rank foundation leads; that digests contain
        an image that reports when they are opened, and that we record when each digest is first
        opened; and that our website uses Cloudflare Web Analytics. It also added our response to
        Do Not Track signals.
      </p>

      <H id="contact">Contact</H>
      <p>
        Privacy questions are answered by {COMPANY}'s founder: <A />
        <br />
        {COMPANY} · {MAILING_ADDRESS}
      </p>
    </LegalShell>
  )
}

const TERMS_CONTENTS = [
  ['service', '1. The service'],
  ['accounts', '2. Accounts'],
  ['billing', '3. Plans, billing, and cancellation'],
  ['acceptable-use', '4. Acceptable use'],
  ['your-data', '5. Your data and our content'],
  ['ai', '6. AI and automated features'],
  ['links', '7. Sources and links'],
  ['changes-service', '8. Changes to the service and availability'],
  ['termination', '9. Termination'],
  ['disclaimers', '10. Disclaimers'],
  ['liability', '11. Limitation of liability'],
  ['indemnification', '12. Indemnification'],
  ['law', '13. Governing law and disputes'],
  ['changes', '14. Changes to these Terms'],
  ['general', '15. General'],
  ['contact', '16. Contact'],
]

export function Terms() {
  return (
    <LegalShell
      title="Terms of Service"
      page="terms"
      description="The terms covering your Praenum subscription: what the service does and does not promise, billing and cancellation, acceptable use, and how the agreement can change."
      effective={TERMS_EFFECTIVE}
      previous={TERMS_PREVIOUS}
      contents={TERMS_CONTENTS}
    >
      <p>
        These Terms of Service ("Terms") govern your use of {COMPANY} ("Praenum", "we", "us"), a
        grant-monitoring service at praenum.com. You accept these Terms by clicking the button
        that creates your account (the signup page says so beside it, with a link to these
        Terms) and by using the service. If you are using {COMPANY} on behalf of an
        organization, you represent that you are authorized to bind that organization, and
        "you" means the organization.
      </p>

      <H id="service">1. The service</H>
      <p>
        {COMPANY} monitors public grant sources (federal grant listings via the official
        Simpler.Grants.gov API and the SAM.gov federal assistance-program catalog, and public
        IRS Form 990 and 990-PF filings). It emails you a digest of grant opportunities and
        foundation funding leads matched to the profile you provide, and gives you an online
        account where you can review, rate and search your matches and the funders behind them.
        Some features depend on your plan, as described on our pricing page.
      </p>
      <p>
        {COMPANY} is an <strong>informational tool</strong>. It surfaces opportunities and
        leads; it is not a grant-writing service, does not submit applications, and does not
        guarantee that you will qualify for, apply to, or receive any grant or funding.
      </p>
      <p>
        <strong>Foundation leads in particular:</strong> these are derived from funders' public
        IRS filings (private-foundation Form 990-PF and, for community foundations and other
        grantmaking public charities, Form 990 Schedule I), which are published months after the
        fact. A lead means a funder's filing shows it has funded organizations with a profile
        like yours and, where the form provides that information, that it accepts applications;
        community-foundation leads carry no filed application statement, so their giving history
        is the only signal. A lead is <strong>not</strong> an open call, an invitation, or an
        endorsement, and {COMPANY} is not affiliated with any foundation or agency it reports
        on. Always verify a funder's current application process, details, deadlines, and
        eligibility with the original source before applying.
      </p>

      <H id="accounts">2. Accounts</H>
      <p>
        You must provide a valid email address and confirm it before digests are sent. You are
        responsible for the accuracy of your profile, the security of your password, and
        activity under your account. You must be at least 18. One account per organization,
        unless your plan expressly allows more.
      </p>

      <H id="billing">3. Plans, billing, and cancellation</H>
      <UL>
        <li>
          Paid plans are billed <strong>monthly in advance</strong> through Stripe at the prices
          shown on our pricing page, and <strong>renew automatically each month until you
          cancel</strong>.
        </li>
        <li>
          You can upgrade, downgrade, or cancel at any time from your account's billing portal.
          Cancellation stops future charges and takes effect at the end of the current billing
          period; access continues until then.
        </li>
        <li>
          If a payment fails, Stripe will retry it and your service continues in the meantime.
          If the payment still cannot be collected, your subscription may be cancelled.
        </li>
        <li>
          Except where required by law, <strong>payments are non-refundable</strong>.
        </li>
        <li>
          <strong>Free trial:</strong> a free trial lasts 14 days. We do not ask for payment
          details to start one, and a trial never turns into a paid plan by itself; to keep
          using {COMPANY} after it ends, choose a plan.
        </li>
        <li>
          <strong>Beta program:</strong> accounts we designate as beta accounts receive the
          service (or the plan named in your invitation) free of charge for the duration of the
          beta, or for any period we tell you. Beta access may be changed or ended at our
          discretion, and beta features may change without notice.
        </li>
        <li>
          <strong>Taxes:</strong> our prices do not include any sales or similar taxes that may
          apply. You are responsible for those taxes, other than taxes on our income.
        </li>
        <li>
          We may change prices with at least 30 days' notice by email; changes apply from your
          next billing cycle after the notice period.
        </li>
      </UL>

      <H id="acceptable-use">4. Acceptable use</H>
      <p>You agree not to:</p>
      <UL>
        <li>
          resell, redistribute, or republish the digests or the underlying matched data, except
          where your plan expressly allows it;
        </li>
        <li>
          scrape, bulk-export, or use automated means to extract data from the service,
          including to build a dataset or database or to train or improve an AI model;
        </li>
        <li>
          reverse engineer the service, or get around any limit on access, such as a feature
          that belongs to a different plan;
        </li>
        <li>share account credentials outside your organization;</li>
        <li>
          set up or use an account for an organization you do not represent, or misstate which
          organization you are;
        </li>
        <li>attempt to disrupt, overload, or gain unauthorized access to the service;</li>
        <li>use the service in violation of applicable law.</li>
      </UL>
      <p>
        Using what you find in {COMPANY} in your own work, for example quoting a foundation's
        details in a letter of inquiry or drafting that letter with an AI tool, is not a breach
        of these rules.
      </p>
      <p>
        We may suspend or terminate accounts that violate these Terms, with notice where
        practicable.
      </p>

      <H id="your-data">5. Your data and our content</H>
      <p>
        Your profile and feedback remain yours; you grant us the license needed to operate the
        service: matching, sending your digests, showing your matches, and improving relevance.
        That includes using your 👍/👎 marks, together with other customers', to test and
        improve how our matching works for everyone.
      </p>
      <p>
        If you send us ideas or suggestions about {COMPANY}, we may use them without any
        obligation to you.
      </p>
      <p>
        The service's software, design, and compiled and derived data presentations are ours.
        Grant and foundation source data is public information; we make no claim over it, and
        no warranty about it (see §10).
      </p>

      <H id="ai">6. AI and automated features</H>
      <p>
        Matching is automated. It uses rule-based scoring, machine-learning models that run on
        our own servers (including the language model that writes the short "why this matched"
        explanations), and a third-party language model, Anthropic's Claude, which ranks the
        foundation leads we find for you. Our <Link to="/privacy" className="text-brand-600 underline">Privacy
        Policy</Link> describes exactly what each one receives.
      </p>
      <p>
        Automated output can be wrong. An explanation can mischaracterize a funder, and a
        ranking can place a weak lead above a strong one. Use your own judgment about what to
        act on, and check the original source.
      </p>
      <p>
        You can ask us not to use Anthropic's model for your organization by emailing <A />.
        Your leads are then ranked by our own scoring alone.
      </p>

      <H id="links">7. Sources and links</H>
      <p>
        Digests and your account link to websites we do not control, such as foundations' own
        sites, Grants.gov and nonprofit-data sites. We are not responsible for their content,
        accuracy or practices, and a link is not an endorsement.
      </p>

      <H id="changes-service">8. Changes to the service and availability</H>
      <p>
        We regularly improve how matching works, and we may add, change or remove features. If
        we remove a feature that is part of your paid plan, we will tell you before it happens,
        and you may cancel.
      </p>
      <p>
        We do not promise that the service will always be available. We take it offline for
        maintenance from time to time, and outages can happen.
      </p>

      <H id="termination">9. Termination</H>
      <p>
        You can stop using the service and cancel at any time. We may terminate or suspend the
        service for breach of these Terms, non-payment, or if we discontinue the product; if we
        discontinue paid service, we will refund the unused portion of your current billing
        period (the exception to §3's no-refund rule). What happens to your information when
        your account ends is described in our Privacy Policy.
      </p>

      <H id="disclaimers">10. Disclaimers</H>
      <Conspicuous>
        <p>
          The service is provided "as is" and "as available." To the extent the law allows, we
          disclaim all implied warranties, including the implied warranties of merchantability,
          fitness for a particular purpose, and non-infringement.
        </p>
        <p>
          We work to surface relevant, timely opportunities, but we do not warrant that the
          service is uninterrupted or error-free, that any listing is complete, current, or
          accurate, that a foundation still accepts applications, or that you are eligible for
          anything we surface. Public filings and government feeds lag and contain errors. Parts
          of the service are automated (see §6), and explanations are checked against the source
          data before display but may still contain errors or mischaracterize fit. Nothing in the
          service is legal, tax, or financial advice. Always verify with the original source
          before relying on anything in a digest.
        </p>
      </Conspicuous>

      <H id="liability">11. Limitation of liability</H>
      <Conspicuous>
        <p>
          To the maximum extent permitted by law: we are not liable for indirect, incidental,
          special, or consequential damages, or for any grant or funding opportunity that is
          missed, misreported, expired, or not awarded. Our total liability for all claims in any
          12-month period is limited to the greater of the amount you paid us in that period and
          $100.
        </p>
      </Conspicuous>

      <H id="indemnification">12. Indemnification</H>
      <p>
        You will indemnify us against third-party claims arising from your misuse of the service
        or violation of these Terms.
      </p>

      <H id="law">13. Governing law and disputes</H>
      <p>
        These Terms are governed by the laws of the Commonwealth of Massachusetts, without
        regard to conflict-of-law rules, and disputes will be resolved in the state or federal
        courts located in Massachusetts.
      </p>

      <H id="changes">14. Changes to these Terms</H>
      <p>
        We may update these Terms. For material changes we will give notice by email or in the
        digest at least 14 days before they take effect; continued use after the effective date
        is acceptance. The current version always lives at praenum.com/terms.
      </p>

      <H id="general">15. General</H>
      <UL>
        <li>
          <strong>Assignment.</strong> You may not transfer these Terms or your account without
          our written consent. We may transfer these Terms to a successor to the {COMPANY}{' '}
          business, including a company formed to operate it, or in a merger or sale; we will
          tell you if that happens.
        </li>
        <li>
          <strong>Events beyond our control.</strong> We are not responsible for a delay or
          failure caused by events beyond our reasonable control, such as a power, internet or
          service-provider outage, or a natural disaster.
        </li>
        <li>
          <strong>Notices.</strong> You agree that we may give you notices, including notices of
          changes to these Terms, by email to your account address or in the digest, and that
          these satisfy any requirement that a notice be in writing.
        </li>
        <li>
          <strong>Entire agreement.</strong> These Terms, our Privacy Policy, and the plan you
          choose are the whole agreement between you and us about {COMPANY}.
        </li>
        <li>
          <strong>Severability and waiver.</strong> If a court finds part of these Terms
          unenforceable, the rest still applies. Our not enforcing a term is not a waiver of it.
        </li>
        <li>
          <strong>Survival.</strong> Sections 5 and 9 to 13, and any amount you owe us, survive
          the end of your account.
        </li>
        <li>
          <strong>Independent parties.</strong> Nothing in these Terms makes either of us the
          other's partner, agent or employee.
        </li>
      </UL>

      <H id="contact">16. Contact</H>
      <p>
        Questions about these Terms: <A />.
        <br />
        {COMPANY} · {MAILING_ADDRESS}
      </p>
    </LegalShell>
  )
}
